import {
  memo,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type RefObject
} from 'react';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { massiveDetailColumnConfig } from '@/app/KeywordResearch/config/massiveDetailColumnConfig';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import massiveApi, {
  useClusterFromTaskMutation,
  useGetMassiveStatusQuery,
  useLazyGetDiagramQuery
} from '@/app/KeywordResearch/api/massiveApi';
import ClusterFromTaskButton from '@/app/KeywordResearch/components/ClusterFromTaskButton';
import {
  Button,
  Card,
  Col,
  Row
} from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { MdContentCopy } from 'react-icons/md';
import { successMessage, warningMessage } from '@/utils/toast';
import IconAlert from '@/components/common/IconAlert';
import { FaFileExcel } from 'react-icons/fa';
import downloadxls from '@/helpers/downloadxls';
import MassiveProgressBar from '../components/keyword-massive/MassiveProgressBar';
import MassiveExcludedKeywordsModal from '../components/keyword-massive/MassiveExcludedKeywordsModal';
import {
  MASSIVE_LOW_RESULTS_THRESHOLD,
  MASSIVE_LOW_RESULTS_TIPS
} from '../components/keyword-massive/massiveFormConfig';
import Tree from '../components/keyword-clustring/components/Tree';
import ClusterViewModeSwitcher from '../components/keyword-clustring/components/ClusterViewModeSwitcher';
import {
  MassiveDiagramNode,
  MassiveKeywordItem
} from '@/app/KeywordResearch/types/massiveApiTypes';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type ViewChartMode = 'grid' | 'basic' | 'radial' | 'card';

type CardMode = 'card_parent' | 'card_grand_parent';

const MASSIVE_VIEW_MODES = [
  { id: 'grid', label: 'جدولی', icon: 'list-ul' },
  { id: 'basic', label: 'پایه', icon: 'folder2' },
  { id: 'radial', label: 'مرکزی', icon: 'bullseye' },
  { id: 'card', label: 'کارتی', icon: 'subtract' }
];

type AgGridRef = RefObject<{
  api?: {
    applyTransaction: (transaction: { add?: unknown[] }) => void;
  };
} | null>;

type AgGridWrapperProps = {
  columns: unknown[];
  height: string;
  data?: MassiveKeywordItem[];
  ref?: AgGridRef;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const groupByGrandParent = (keywords: MassiveKeywordItem[]) => {
  const grouped: Record<string, MassiveKeywordItem[]> = {};

  keywords.forEach(keywordObj => {
    const grandParent = keywordObj.grand_parent_keyword;

    if (!grouped[grandParent]) {
      grouped[grandParent] = [];
    }

    grouped[grandParent].push(keywordObj);
  });
  return Object.values(grouped);
};

const groupByParent = (keywords: MassiveKeywordItem[]) => {
  const grouped: Record<string, MassiveKeywordItem[]> = {};

  keywords.forEach(keywordObj => {
    const parent = keywordObj.parent_keyword;

    if (!grouped[parent]) {
      grouped[parent] = [];
    }

    grouped[parent].push(keywordObj);
  });
  return Object.values(grouped);
};

const MassiveDetails = () => {
  const [cardMode, setCardMode] = useState<CardMode>('card_parent');
  const { id = '' } = useParams<{ id: string }>();
  const gridRef = useRef<{
    api?: {
      applyTransaction: (transaction: { add?: unknown[] }) => void;
    };
  } | null>(null);
  const dispatch = useDispatch();
  const [gridData, setGridData] = useState<MassiveKeywordItem[]>([]);
  const [diagramData, setDiagramData] = useState<MassiveDiagramNode | null>(
    null
  );
  const [viewChartMode, setViewChartMode] = useState<ViewChartMode>('grid');
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  useGetUserInfoQuery();
  const [getDiagram, { isLoading: isLoadingCharts }] = useLazyGetDiagramQuery();
  const { data: statusMassive, isLoading } = useGetMassiveStatusQuery(id);
  const [clusterFromTask, { isLoading: isClustering }] =
    useClusterFromTaskMutation();

  useEffect(() => {
    if (statusMassive) {
      if (statusMassive.data.status === 0) {
        // getMassiveSuggestion({ task_id: id });
      } else if (statusMassive.data.status === 4) {
        setGridData(statusMassive.data.keywords);
      } else if (
        statusMassive.data.status === 6 ||
        statusMassive.data.status === 49
      ) {
        getDiagram(id)
          .unwrap()
          .then(res => setDiagramData(res?.data?.data ?? null));
      }
    }
  }, [statusMassive, getDiagram, id]);

  useEffect(() => {
    setBreadcrumbData([{ text: 'کلمات انبوه', href: '#' }]);
    return () => {
      dispatch(
        massiveApi.util.invalidateTags(['massiveStatus', 'historyMassive'])
      );
    };
  }, [dispatch, setBreadcrumbData]);

  const [isHelperDismissed, setIsHelperDismissed] = useState(false);
  const [showExcludedModal, setShowExcludedModal] = useState(false);
  const [socketFinished, setSocketFinished] = useState(false);
  const [socketKeywordCount, setSocketKeywordCount] = useState(0);
  const excludedCount = statusMassive?.data?.excluded_count ?? 0;

  const isChartReady =
    statusMassive?.data?.status === 6 || statusMassive?.data?.status === 49;

  const keywords = statusMassive?.data?.keywords ?? [];
  const isTaskFinished = isChartReady || socketFinished;
  const finishedKeywordCount = isChartReady
    ? keywords.length
    : socketKeywordCount;
  const tableKeywordCount = isChartReady
    ? keywords.length
    : Math.max(gridData.length, socketKeywordCount);
  const shouldShowLowResultsHint =
    isTaskFinished &&
    finishedKeywordCount < MASSIVE_LOW_RESULTS_THRESHOLD;

  const handleViewModeChange = (modeId: string) => {
    if (modeId === 'grid') {
      setViewChartMode('grid');
      return;
    }
    if (!isChartReady) {
      warningMessage(
        'درحال دریافت کلمات هستیم، لطفا پس از اتمام عملیات صفحه رو رفرش کنید تا این بخش رو مشاهده کنید.'
      );
      return;
    }
    setViewChartMode(modeId as ViewChartMode);
  };

  return (
    <>
      {statusMassive &&
        statusMassive.data.status !== 6 &&
        statusMassive.data.status !== 49 && (
          <div className="my-3">
            <MassiveProgressBar
              lastStatus={statusMassive.data.status}
              task_id={id}
              gridRef={gridRef}
              onTaskFinished={keywordCount => {
                setSocketFinished(true);
                setSocketKeywordCount(keywordCount);
              }}
            />
            {!isHelperDismissed && (
              <IconAlert
                onClose={() => setIsHelperDismissed(true)}
                dismissible={true}
                className="mt-3"
                variant="info"
              >
                درحال دریافت کلمات هستیم، لطفا پس از اتمام عملیات صفحه رو رفرش
                کنید تا نمایش های دیگه رو ببینید.
              </IconAlert>
            )}
          </div>
        )}
      <div className="d-flex align-items-center justify-content-center mt-3 mb-3 px-2">
        <ClusterViewModeSwitcher
          value={viewChartMode}
          onChange={handleViewModeChange}
          modes={MASSIVE_VIEW_MODES.map(mode => ({
            ...mode,
            disabled: isLoading && mode.id !== 'grid'
          }))}
          aria-label="نوع نمایش کلمات انبوه"
        />
      </div>
      <div>
        {viewChartMode === 'grid' && (
          <>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
              <ClusterFromTaskButton
                taskId={id}
                keywordCount={tableKeywordCount}
                mutate={clusterFromTask}
                isClustering={isClustering}
              />
              <div>
                {excludedCount > 0 && (
                  <Button
                    variant="link"
                    className="p-0 fs--1 text-decoration-none"
                    onClick={() => setShowExcludedModal(true)}
                  >
                    کلمات فیلتر شده ({excludedCount})
                  </Button>
                )}
              </div>
            </div>
            {shouldShowLowResultsHint && (
              <IconAlert variant="warning" className="mb-3">
                <p className="mb-2">
                  اگر تعداد کلمات پیشنهادی کم است، این موارد را امتحان کنید:
                </p>
                <div className="d-flex flex-column gap-1">
                  {MASSIVE_LOW_RESULTS_TIPS.map((tip, index) => (
                    <div key={tip}>
                      {['۱', '۲', '۳'][index]}. {tip}
                    </div>
                  ))}
                </div>
              </IconAlert>
            )}
            {!isLoading ? (
              <TypedAgGridWrapper
                columns={massiveDetailColumnConfig}
                height="746px"
                data={
                  isChartReady ? statusMassive?.data?.keywords : gridData
                }
                ref={gridRef}
              />
            ) : (
              <AgGridSkeleton height="696px" />
            )}
          </>
        )}
        {viewChartMode === 'card' && (
          <>
            <div className="position-relative d-flex align-items-start justify-content-between gap-3 mb-3 mt-4">
              <div
                style={{ width: '170px' }}
                className="d-flex align-items-center gap-2"
              >
                <label htmlFor="mode-select" className="form-label text-nowrap">
                  نوع نمایش
                </label>
                <select
                  id="mode-select"
                  className="form-select"
                  value={cardMode}
                  onChange={e => setCardMode(e.target.value as CardMode)}
                >
                  <option value="card_parent">والد</option>
                  <option value="card_grand_parent">ریشه</option>
                </select>
              </div>
              <Button
                variant="falcon-primary"
                className="d-flex align-items-center gap-1"
                onClick={() => {
                  if (cardMode === 'card_grand_parent') {
                    const groupedData = groupByGrandParent(keywords);
                    const maxKeywordsCount = Math.max(
                      ...groupedData.map(group => group.length)
                    );
                    const transformedData: Record<string, string>[] = Array.from(
                      { length: maxKeywordsCount },
                      () => ({})
                    );

                    groupedData.forEach(group => {
                      const grandParentKeyword = decodeURIComponent(
                        group[0].grand_parent_keyword
                      );

                      group.forEach((item, index) => {
                        transformedData[index][grandParentKeyword] =
                          decodeURIComponent(item.keyword);
                      });
                    });

                    downloadxls(transformedData);
                  } else if (cardMode === 'card_parent') {
                    const groupedData = groupByParent(keywords);
                    const maxKeywordsCount = Math.max(
                      ...groupedData.map(group => group.length)
                    );
                    const transformedData: Record<string, string>[] = Array.from(
                      { length: maxKeywordsCount },
                      () => ({})
                    );

                    groupedData.forEach(group => {
                      const parentKeyword = decodeURIComponent(
                        group[0].parent_keyword
                      );

                      group.forEach((item, index) => {
                        transformedData[index][parentKeyword] =
                          decodeURIComponent(item.keyword);
                      });
                    });

                    downloadxls(transformedData);
                  }
                }}
              >
                <FaFileExcel
                  title="خروجی"
                  style={{ paddingLeft: '3px' }}
                  size={14}
                />
                <span className="d-sm-inline d-none">خروجی&nbsp;</span> Excel
              </Button>
            </div>
            {cardMode === 'card_grand_parent' && (
              <Row className="g-3 my-3">
                {groupByGrandParent(keywords).map(item => (
                  <Col xs={12} md={6} xxl={3} key={item[0].grand_parent_keyword}>
                    <Card className="h-100">
                      <FalconCardHeader
                        title={
                          <div>
                            {decodeURIComponent(item[0].grand_parent_keyword)}
                          </div>
                        }
                        titleTag="h6"
                        className="py-2 fw-bolder"
                        light
                        endEl={
                          <MdContentCopy
                            title="کلمات"
                            onClick={() => {
                              navigator.clipboard
                                .writeText(
                                  decodeURIComponent(
                                    item[0].grand_parent_keyword
                                  ) +
                                    '\n\n' +
                                    [...new Set(item)]
                                      .map(({ keyword }) =>
                                        decodeURIComponent(keyword)
                                      )
                                      .join('\n')
                                )
                                .then(() => {
                                  successMessage('کلمات کپی شدن');
                                });
                            }}
                            className="cursor-pointer little-icon"
                            style={{ paddingLeft: '3px' }}
                            size={18}
                          />
                        }
                      />

                      <Card.Body
                        className="fs--1"
                        style={{ height: 300, overflow: 'auto' }}
                      >
                        {[...new Set(item)].map(({ keyword }) => (
                          <div key={keyword}>{decodeURIComponent(keyword)}</div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
            {cardMode === 'card_parent' && (
              <Row className="g-3 my-3">
                {groupByParent(keywords).map(item => (
                  <Col xs={12} md={6} xxl={3} key={item[0].parent_keyword}>
                    <Card className="h-100">
                      <FalconCardHeader
                        title={
                          <div>
                            {decodeURIComponent(item[0].parent_keyword)}
                          </div>
                        }
                        titleTag="h6"
                        className="py-2 fw-bolder"
                        light
                        endEl={
                          <MdContentCopy
                            title="کلمات"
                            onClick={() => {
                              navigator.clipboard
                                .writeText(
                                  decodeURIComponent(item[0].parent_keyword) +
                                    '\n\n' +
                                    [...new Set(item)]
                                      .map(({ keyword }) =>
                                        decodeURIComponent(keyword)
                                      )
                                      .join('\n')
                                )
                                .then(() => {
                                  successMessage('کلمات کپی شدن');
                                });
                            }}
                            className="cursor-pointer little-icon"
                            style={{ paddingLeft: '3px' }}
                            size={18}
                          />
                        }
                      />

                      <Card.Body
                        className="fs--1"
                        style={{ height: 300, overflow: 'auto' }}
                      >
                        {[...new Set(item)].map(({ keyword }) => (
                          <div key={keyword}>{decodeURIComponent(keyword)}</div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}

        {viewChartMode === 'basic' &&
          (!isLoadingCharts && diagramData ? (
            <Tree title="درخت پایه" data={diagramData} />
          ) : (
            ''
          ))}
        {viewChartMode === 'radial' &&
          (!isLoadingCharts && diagramData ? (
            <Tree data={diagramData} layout="radial" title="درخت مرکزی" />
          ) : (
            ''
          ))}
      </div>
      <MassiveExcludedKeywordsModal
        show={showExcludedModal}
        onHide={() => setShowExcludedModal(false)}
        taskId={id}
      />
    </>
  );
};

export default memo(MassiveDetails);
